import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../baseQuery';
import { 
  IResidentListResponse, 
  IResidentListFormValues,
  IFilterModel,
  ISortModel,
  IPaginationModel
} from '@/types/admin/residentList';
import {
  IResidentDetailsResponse,
  IResidentDetailsParams,
  IUpdateResidentInfoRequest,
  IUpdateResidentInfoResponse,
} from '@/types/admin/residentDetails';
import { IResidentListSummaryResponse } from '@/types/admin/residentSummary';
import { ITransactionListResponse, IAdminTransaction } from '@/types/admin/transactions';


// Helper function to build query string (matching admin-web logic exactly)
const buildResidentListQuery = ({
  sortModel,
  filterModel,
  paginationModel,
  selectedPropertyId,
}: {
  sortModel: ISortModel[];
  filterModel: IFilterModel;
  paginationModel: IPaginationModel;
  selectedPropertyId: number;
}): string => {
  // Field parameter mappings (exactly matching admin-web)
  const fieldParamMappings: { [key: string]: string } = {
    name: "searchQuery",
    lease_status: "leaseStatus",
    unit: "unitNumberQuery",
    last_login: "lastLogin",
    // For filtering, backend expects lastPaymentDate
    last_payment: "lastPaymentDate",
    autopay: "hasRecurringPayments",
    customer_type: "customerType",
  };

  // Separate mapping for sorting: backend expects "lastPayment" key for sortQuery
  const sortFieldParamMappings: { [key: string]: string } = {
    name: "searchQuery",
    lease_status: "leaseStatus",
    unit: "unitNumberQuery",
    last_login: "lastLogin",
    // For sorting, backend expects lastPayment or hasPaymentThisMonth
    last_payment: "lastPayment",
    autopay: "hasRecurringPayments",
    customer_type: "customerType",
  };

  const filteredFilterArgs = filterModel.items.filter((item) => {
    return item.value && item.value !== "";
  });

  const filterArgs = filteredFilterArgs.map((item) => {
    if (item.operator.includes("not")) {
      return `${fieldParamMappings[item.field]}=!${item.value}`;
    }
    if (item.operator.includes("onOrAfter")) {
      return `${fieldParamMappings[item.field]}=>${item.value}`;
    }
    if (item.operator.includes("onOrBefore")) {
      return `${fieldParamMappings[item.field]}=<${item.value}`;
    }
    return `${fieldParamMappings[item.field]}=${item.value}`;
  });

  const combinedFilterArgs: { [key: string]: string[] } = {};
  filterArgs.forEach((arg: string) => {
    const splitArg = arg.split("=");
    if (combinedFilterArgs[splitArg[0]]) {
      combinedFilterArgs[splitArg[0]].push(splitArg[1]);
    } else {
      combinedFilterArgs[splitArg[0]] = [splitArg[1]];
    }
  });

  const offset = paginationModel.page * paginationModel.pageSize;
  const limit = paginationModel.pageSize;

  let paramString = `?propertyId=${selectedPropertyId}&offset=${offset}&limit=${limit}`;
  
  Object.keys(combinedFilterArgs).forEach((key: string) => {
    paramString += `&${key}=${combinedFilterArgs[key].join(",")}`;
  });

  if (sortModel.length) {
    // Backend expects: "-" prefix for ASC, no prefix for DESC
    // This matches the backend logic in helper.ts:237
    const sortKey = sortFieldParamMappings[sortModel[0].field] ?? sortModel[0].field;
    const sortValue =
      sortModel[0].sort === "asc"
        ? `-${sortKey}`  // ASC: add "-" prefix
        : sortKey;        // DESC: no prefix
    paramString += `&sortQuery=${sortValue}`;
  }

  return paramString;
};

// RTK Query API slice (matching admin-web pattern exactly)
export const residentListApi = createApi({
  reducerPath: 'residentListApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['ResidentList', 'ResidentDetails'],
  endpoints: (builder) => ({
    // Get resident list with filtering, sorting, and pagination
    getResidentList: builder.query<IResidentListResponse, {
      sortModel: ISortModel[];
      filterModel: IFilterModel;
      paginationModel: IPaginationModel;
      selectedPropertyId: number;
    }>({
      query: (params) => {
        const queryString = buildResidentListQuery(params);
        const fullUrl = `/api/v1/residents/list${queryString}`;
        return {
          url: fullUrl,
          method: 'GET',
        };
      },
      providesTags: (result, error, arg) => [
        { type: 'ResidentList', id: `property-${arg.selectedPropertyId}` },
        { type: 'ResidentList', id: 'LIST' },
      ],
    }),

    // Get resident details
    getResidentDetails: builder.query<IResidentDetailsResponse, IResidentDetailsParams>({
      query: ({ accountId, leaseId, mode }) => ({
        url: `/api/v1/residents/details?accountId=${accountId}&leaseId=${leaseId}${mode ? `&mode=${mode}` : ''}`,
        method: 'GET',
      }),
      providesTags: (result, error, { accountId, leaseId }) => [
        { type: 'ResidentDetails', id: `${accountId}-${leaseId}` },
      ],
    }),

    // Get resident transaction info
    getResidentTransactionInfo: builder.query<ITransactionListResponse, { accountId: number; leaseId: number }>({
      query: ({ accountId, leaseId }) => ({
        url: `/api/v1/residents/transaction-info?accountId=${accountId}&leaseId=${leaseId}`,
        method: 'GET',
      }),
      providesTags: (result, error, { accountId, leaseId }) => [
        { type: 'ResidentDetails', id: `transactions-${accountId}-${leaseId}` },
      ],
      keepUnusedDataFor: 300, // Cache for 5 minutes
    }),

    // Get resident payment info
    getResidentPaymentInfo: builder.query<any, { accountId: number; leaseId: number }>({
      query: ({ accountId, leaseId }) => ({
        url: `/api/v1/residents/payment-info?accountId=${accountId}&leaseId=${leaseId}`,
        method: 'GET',
      }),
    }),

    // Get resident list summary (counts + autopay/move-out aggregates)
    getResidentListSummary: builder.query<IResidentListSummaryResponse, { propertyId?: number }>({
      query: ({ propertyId }) => ({
        url: '/api/v1/residents/list-summary',
        method: 'GET',
        params: propertyId ? { propertyId } : undefined,
      }),
      providesTags: (result, error, arg) => [
        { type: 'ResidentList', id: `summary-property-${arg.propertyId ?? 'all'}` },
      ],
    }),

    // Get resident payments (Resio payment history)
    getResidentPayments: builder.query<{ payments: IAdminTransaction[] }, { accountId: number; leaseId: number }>({
      query: ({ accountId, leaseId }) => ({
        url: `/api/v1/residents/payments?accountId=${accountId}&leaseId=${leaseId}`,
        method: 'GET',
      }),
      providesTags: (result, error, { accountId, leaseId }) => [
        { type: 'ResidentDetails', id: `payments-${accountId}-${leaseId}` },
      ],
      keepUnusedDataFor: 300, // Cache for 5 minutes
    }),

    // Update lease payments (toggle payments)
    updateLeasePayments: builder.mutation<any, { 
      accountId: number; 
      leaseId: number; 
      blocked: boolean 
    }>({
      query: ({ accountId, leaseId, blocked }) => ({
        url: `/api/v1/residents/toggle-payments?accountId=${accountId}&leaseId=${leaseId}&value=${blocked}`,
        method: 'PATCH',
      }),
      invalidatesTags: (result, error, { accountId, leaseId }) => [
        { type: 'ResidentDetails', id: `${accountId}-${leaseId}` },
        { type: 'ResidentList', id: 'LIST' },
      ],
    }),

    // Update resident info
    updateResidentInfo: builder.mutation<IUpdateResidentInfoResponse, IUpdateResidentInfoRequest>({
      query: (body) => ({
        url: '/api/v1/residents/update-resident',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['ResidentDetails', 'ResidentList'],
    }),
  }),
});

// Export hooks for usage in functional components
export const {
  useGetResidentListQuery,
  useLazyGetResidentListQuery,
  useGetResidentDetailsQuery,
  useLazyGetResidentDetailsQuery,
  useGetResidentListSummaryQuery,
  useLazyGetResidentListSummaryQuery,
  useGetResidentTransactionInfoQuery,
  useLazyGetResidentTransactionInfoQuery,
  useGetResidentPaymentInfoQuery,
  useLazyGetResidentPaymentInfoQuery,
  useGetResidentPaymentsQuery,
  useLazyGetResidentPaymentsQuery,
  useUpdateLeasePaymentsMutation,
  useUpdateResidentInfoMutation,
} = residentListApi;
